import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';

const RelatorioPacotesTab = ({ isActive }) => {
    const { currentEntidade } = useEntidadeContext();
    const [dadosRelatorio, setDadosRelatorio] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [pacientesOptions, setPacientesOptions] = useState([]);
    const [selectedPaciente, setSelectedPaciente] = useState(null);
    const [isLoadingPacientes, setIsLoadingPacientes] = useState(false);

    useEffect(() => {
        const fetchPacientes = async () => {
            if (!isActive || !currentEntidade || pacientesOptions.length > 0) return;
            setIsLoadingPacientes(true);
            try {
                const response = await axios.get(`/api/entidades/${currentEntidade.id}/pacientes/basic-info`);
                const options = (response.data.pacientes || []).map(p => ({
                    value: p.id,
                    label: `${p.nome} (${p.cpf || 'Sem CPF'})`
                }));
                setPacientesOptions(options);
            } catch (err) {
                console.error("Erro ao buscar pacientes para filtro:", err);
            } finally {
                setIsLoadingPacientes(false);
            }
        };
        fetchPacientes();
    }, [isActive, currentEntidade, pacientesOptions.length]);

    useEffect(() => {
        const fetchRelatorio = async () => {
            if (!isActive || !currentEntidade) return;
            setIsLoading(true);
            setError(null);
            try {
                const params = { entidade_id: currentEntidade.id };
                if (selectedPaciente) params.paciente_id = selectedPaciente.value;

                const response = await axios.get(`/api/relatorios/pacotes/sessoes-restantes`, { params });
                const dadosOrdenados = (response.data.dados || []).sort((a, b) =>
                    (a.paciente_nome || '').localeCompare(b.paciente_nome || '')
                );
                setDadosRelatorio(dadosOrdenados);
            } catch (err) {
                console.error("Erro ao buscar relatório de pacotes:", err);
                setError("Não foi possível carregar o relatório. Tente novamente mais tarde.");
                setDadosRelatorio([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRelatorio();
    }, [isActive, currentEntidade, selectedPaciente]);

    /* ── estilos do react-select ── */
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '8px',
            border: `1px solid ${state.isFocused ? '#3b82f6' : '#e5e7eb'}`,
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
            fontSize: '13px',
            minHeight: '38px',
            fontFamily: "'Inter','Segoe UI',sans-serif",
            '&:hover': { borderColor: '#3b82f6' },
        }),
        placeholder: base => ({ ...base, color: '#9ca3af', fontSize: '13px' }),
        option: (base, state) => ({
            ...base,
            fontSize: '13px',
            backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : '#fff',
            color: state.isSelected ? '#fff' : '#374151',
        }),
        singleValue: base => ({ ...base, color: '#374151', fontSize: '13px' }),
    };

    return (
        <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>

            {/* ── FILTRO ── */}
            <div style={{
                backgroundColor: '#fff', borderRadius: '12px',
                border: '1px solid #e5e7eb', padding: '20px 24px',
                marginBottom: '20px',
            }}>
                <p style={{
                    fontSize: '10px', fontWeight: '700', color: '#9ca3af',
                    textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px',
                }}>
                    Filtros
                </p>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {/* Select de paciente */}
                    <div style={{ flex: '1 1 280px', minWidth: '200px' }}>
                        <label style={{
                            fontSize: '11px', fontWeight: '700', color: '#6b7280',
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                            display: 'block', marginBottom: '5px',
                        }}>
                            Paciente (opcional)
                        </label>
                        <Select
                            options={pacientesOptions}
                            value={selectedPaciente}
                            onChange={setSelectedPaciente}
                            isClearable
                            isLoading={isLoadingPacientes}
                            placeholder="Digite para buscar um paciente..."
                            noOptionsMessage={() => 'Nenhum paciente encontrado'}
                            styles={selectStyles}
                        />
                    </div>

                    {/* Botão limpar */}
                    <button
                        onClick={() => setSelectedPaciente(null)}
                        disabled={!selectedPaciente}
                        style={{
                            padding: '9px 16px', borderRadius: '8px', height: '38px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: selectedPaciente ? '#fff' : '#f9fafb',
                            color: selectedPaciente ? '#374151' : '#d1d5db',
                            cursor: selectedPaciente ? 'pointer' : 'not-allowed',
                            fontSize: '13px', fontWeight: '500',
                            fontFamily: "'Inter','Segoe UI',sans-serif",
                            display: 'flex', alignItems: 'center', gap: '6px',
                            transition: 'all 0.15s',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Limpar filtro
                    </button>
                </div>
            </div>

            {/* ── CONTEÚDO ── */}
            {isLoading ? (
                <div style={{
                    backgroundColor: '#fff', borderRadius: '12px',
                    border: '1px solid #e5e7eb', padding: '60px 24px',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: '12px',
                }}>
                    <Spinner animation="border" variant="primary" style={{ width: '32px', height: '32px' }} />
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Carregando relatório...</p>
                </div>

            ) : error ? (
                <div style={{
                    backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '12px', padding: '20px 24px',
                    display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                    <svg width="20" height="20" fill="none" stroke="#dc2626" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p style={{ margin: 0, fontSize: '13px', color: '#dc2626', fontWeight: '500' }}>{error}</p>
                </div>

            ) : dadosRelatorio.length > 0 ? (
                <div style={{
                    backgroundColor: '#fff', borderRadius: '12px',
                    border: '1px solid #e5e7eb', overflow: 'hidden',
                }}>
                    {/* Cabeçalho da tabela */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
                        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="16" height="16" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                                Pacotes com Sessões Restantes
                            </span>
                        </div>
                        <span style={{
                            fontSize: '11px', fontWeight: '700', color: '#2563eb',
                            backgroundColor: '#eff6ff', padding: '3px 10px',
                            borderRadius: '999px', border: '1px solid #bfdbfe',
                        }}>
                            {dadosRelatorio.length} {dadosRelatorio.length === 1 ? 'registro' : 'registros'}
                        </span>
                    </div>

                    {/* Tabela */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                    {['Paciente', 'Serviço (Pacote)', 'Sessões Restantes', 'Data da Compra'].map((h, i) => (
                                        <th key={h} style={{
                                            padding: '10px 16px',
                                            fontSize: '11px', fontWeight: '700',
                                            color: '#64748b', textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            textAlign: i === 2 ? 'center' : 'left',
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dadosRelatorio.map((item, index) => (
                                    <tr key={index} style={{
                                        borderBottom: '1px solid #f1f5f9',
                                        backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                                        transition: 'background 0.1s',
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eff6ff'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#fafafa'}
                                    >
                                        {/* Paciente */}
                                        <td style={{ padding: '12px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '30px', height: '30px', borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb' }}>
                                                        {(item.paciente_nome || '?')[0].toUpperCase()}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                                                    {item.paciente_nome}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Serviço */}
                                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>
                                            {item.servico_nome}
                                        </td>

                                        {/* Sessões restantes */}
                                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                minWidth: '32px', height: '24px', borderRadius: '999px',
                                                fontSize: '12px', fontWeight: '700', padding: '0 10px',
                                                backgroundColor: item.sessoes_restantes <= 2
                                                    ? '#fef2f2' : item.sessoes_restantes <= 5
                                                    ? '#fffbeb' : '#f0fdf4',
                                                color: item.sessoes_restantes <= 2
                                                    ? '#dc2626' : item.sessoes_restantes <= 5
                                                    ? '#d97706' : '#16a34a',
                                                border: `1px solid ${item.sessoes_restantes <= 2
                                                    ? '#fecaca' : item.sessoes_restantes <= 5
                                                    ? '#fde68a' : '#bbf7d0'}`,
                                            }}>
                                                {item.sessoes_restantes}
                                            </span>
                                        </td>

                                        {/* Data da compra */}
                                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                                            {item.data_compra}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            ) : (
                <div style={{
                    backgroundColor: '#fff', borderRadius: '12px',
                    border: '1px dashed #e5e7eb', padding: '60px 24px',
                    textAlign: 'center',
                }}>
                    <svg width="40" height="40" fill="none" stroke="#d1d5db" viewBox="0 0 24 24"
                        style={{ display: 'block', margin: '0 auto 12px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <p style={{ margin: 0, fontSize: '14px', color: '#9ca3af', fontWeight: '500' }}>
                        Nenhum pacote com sessões restantes encontrado
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#d1d5db' }}>
                        {selectedPaciente ? 'Tente remover o filtro de paciente.' : 'Todos os pacotes foram utilizados.'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default RelatorioPacotesTab;
